import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  Clock3,
  LifeBuoy,
  Mail,
  MessageSquare,
  Send,
  Sparkles,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ISSUE_TYPES = [
  { value: "TECHNICAL_PROBLEM", label: "Teknik Sorun" },
  { value: "PAYMENT_AND_SUBSCRIPTION", label: "Ödeme ve Abonelik" },
  { value: "CONTENT_UPDATE", label: "Menü / İçerik Güncellemesi" },
  { value: "APPEARANCE", label: "Tasarım ve Görünüm" },
  { value: "FEATURE_REQUEST", label: "Yeni Özellik Talebi" },
  { value: "OTHER", label: "Diğer" },
];

const PRIORITIES = [
  { value: "LOW", label: "Düşük" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "Yüksek" },
  { value: "CRITICAL", label: "Kritik" },
];

const INITIAL_FORM = {
  subject: "",
  issueType: "",
  priority: "LOW",
  message: "",
};

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem("user");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    console.error("Kullanıcı bilgisi okunamadı:", error);
    return null;
  }
};

const getUserSupportInfo = (user) => {
  if (!user) return {};

  return {
    userId: user.id || user.Id || user.userId || user.UserId || null,
    restaurantId:
      user.restaurantId ||
      user.RestaurantId ||
      user.restaurant?.id ||
      user.restaurant?.Id ||
      null,
    restaurantName:
      user.restaurantName ||
      user.RestaurantName ||
      user.restaurant?.name ||
      user.restaurant?.Name ||
      user.business?.name ||
      user.business?.Name ||
      "",
    contactName:
      [user.firstName || user.FirstName, user.lastName || user.LastName]
        .filter(Boolean)
        .join(" ") ||
      user.fullName ||
      user.FullName ||
      user.username ||
      user.UserName ||
      "",
    contactEmail:
      user.email || user.Email || user.username || user.UserName || "",
    contactPhone:
      user.phone ||
      user.Phone ||
      user.userPhone ||
      user.UserPhone ||
      user.restaurant?.phone ||
      user.restaurant?.Phone ||
      user.business?.phone ||
      user.business?.Phone ||
      "",
  };
};

const getConstantValue = (item) =>
  item?.value ??
  item?.Value ??
  item?.constantValue ??
  item?.ConstantValue ??
  "";

const getConstantLabel = (item) =>
  item?.name ??
  item?.Name ??
  item?.label ??
  item?.Label ??
  getConstantValue(item);

const normalizeConstantItems = (response, fallback = []) => {
  const rawItems = Array.isArray(response)
    ? response
    : Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.items)
    ? response.data.items
    : [];

  const normalizedItems = rawItems
    .map((item) => ({
      value: String(getConstantValue(item) || "").trim(),
      label: String(getConstantLabel(item) || "").trim(),
    }))
    .filter((item) => item.value);

  return normalizedItems.length ? normalizedItems : fallback;
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;

  if (typeof data === "string") return data;

  if (data?.message) return data.message;
  if (data?.Message) return data.Message;
  if (data?.title) return data.title;
  if (data?.Title) return data.Title;

  if (data?.errors && typeof data.errors === "object") {
    const firstError = Object.values(data.errors).flat()?.[0];
    if (firstError) return firstError;
  }

  if (error?.message) return error.message;

  return "İşlem sırasında bir hata oluştu.";
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const normalizeTicket = (ticket) => ({
  localId: ticket?.id || ticket?.Id || Date.now(),
  id: ticket?.id || ticket?.Id || null,
  subject: ticket?.title || ticket?.Title || "Başlıksız talep",
  issueType: ticket?.typeConstantValue || ticket?.TypeConstantValue || "",
  issueTypeLabel: ticket?.typeConstantName || ticket?.TypeConstantName || "",
  priority: ticket?.priorityConstantValue || ticket?.PriorityConstantValue || "",
  priorityLabel: ticket?.priorityConstantName || ticket?.PriorityConstantName || "",
  message:
    ticket?.message ||
    ticket?.Message ||
    "Bu talebin açıklaması liste yanıtında dönmüyor.",
  userEmail: ticket?.userEmail || ticket?.UserEmail || "",
  userFullname: ticket?.userFullname || ticket?.UserFullname || "",
  userPhone: ticket?.userPhone || ticket?.UserPhone || "",
  createdAt: ticket?.createdDate || ticket?.CreatedDate || new Date().toISOString(),
  status: "Gönderildi",
});

export default function SupportPage() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [recentTickets, setRecentTickets] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [issueTypes, setIssueTypes] = useState(ISSUE_TYPES);
  const [priorities, setPriorities] = useState(PRIORITIES);
  const [constantsLoading, setConstantsLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [mobileTicketOpen, setMobileTicketOpen] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);

  const loadTicketsFromApi = async () => {
    setTicketsLoading(true);

    try {
      const response = await api.getTickets({
        page: 0,
        pageSize: 1000,
      });

      const items = Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data?.items)
        ? response.data.items
        : Array.isArray(response)
        ? response
        : [];

      setRecentTickets(items.map(normalizeTicket));
    } catch (error) {
      console.error("Destek talepleri yüklenemedi:", error);
      toast.error(getApiErrorMessage(error) || "Destek talepleri yüklenemedi.");
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    const user = getStoredUser();
    setUserInfo(getUserSupportInfo(user));

    loadTicketsFromApi();

    const loadConstants = async () => {
      setConstantsLoading(true);

      try {
        const [ticketTypesRes, prioritiesRes] = await Promise.all([
          api.getConstants({ type: "TICKET_TYPE" }),
          api.getConstants({ type: "TICKET_PRIORITY" }),
        ]);

        const normalizedIssueTypes = normalizeConstantItems(
          ticketTypesRes,
          ISSUE_TYPES
        );

        const normalizedPriorities = normalizeConstantItems(
          prioritiesRes,
          PRIORITIES
        );

        setIssueTypes(normalizedIssueTypes);
        setPriorities(normalizedPriorities);

        setFormData((prev) => {
          const currentPriorityExists = normalizedPriorities.some(
            (item) => item.value === prev.priority
          );

          return {
            ...prev,
            priority: currentPriorityExists
              ? prev.priority
              : normalizedPriorities[0]?.value || "LOW",
          };
        });
      } catch (error) {
        console.error("Constant verileri yüklenemedi:", error);
        setIssueTypes(ISSUE_TYPES);
        setPriorities(PRIORITIES);
        toast.error("Talep türü ve öncelik bilgileri yüklenemedi.");
      } finally {
        setConstantsLoading(false);
      }
    };

    loadConstants();
  }, []);

  const stats = useMemo(() => {
    const criticalCount = recentTickets.filter(
      (ticket) => ticket.priority === "CRITICAL"
    ).length;

    return {
      total: recentTickets.length,
      critical: criticalCount,
    };
  }, [recentTickets]);

  const selectedIssueTypeLabel =
    (issueTypes.length ? issueTypes : ISSUE_TYPES).find(
      (item) => item.value === formData.issueType
    )?.label || "Bir kategori seçin";

  const selectedPriorityLabel =
    (priorities.length ? priorities : PRIORITIES).find(
      (item) => item.value === formData.priority
    )?.label || formData.priority;

  const resetMobileTicket = () => {
    setFormData({
      ...INITIAL_FORM,
      priority: priorities?.[0]?.value || "LOW",
    });
    setMobileStep(1);
  };

  const openMobileTicket = () => {
    resetMobileTicket();
    setMobileTicketOpen(true);
  };

  const closeMobileTicket = () => {
    setMobileTicketOpen(false);
    resetMobileTicket();
  };

  const handleMobileNext = () => {
    if (!formData.subject.trim()) {
      toast.error("Lütfen talep başlığını girin.");
      return;
    }

    if (!formData.issueType) {
      toast.error("Lütfen talep türünü seçin.");
      return;
    }

    if (!formData.priority) {
      toast.error("Lütfen öncelik seçin.");
      return;
    }

    setMobileStep(2);
  };

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.subject.trim()) {
      toast.error("Lütfen talep başlığını girin.");
      return false;
    }

    if (!formData.issueType) {
      toast.error("Lütfen talep türünü seçin.");
      return false;
    }

    if (!formData.priority) {
      toast.error("Lütfen öncelik seçin.");
      return false;
    }

    if (!formData.message.trim() || formData.message.trim().length < 20) {
      toast.error("Talep açıklaması en az 20 karakter olmalı.");
      return false;
    }

    return true;
  };

  const getPriorityLabel = (value) => {
    const allPriorities = priorities.length ? priorities : PRIORITIES;
    return allPriorities.find((item) => item.value === value)?.label || value;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        title: formData.subject.trim(),
        typeConstantValue: formData.issueType,
        priorityConstantValue: formData.priority,
        message: formData.message.trim(),
      };

      await api.addTicket(payload);

      await loadTicketsFromApi();
      setFormData({
        ...INITIAL_FORM,
        priority: priorities?.[0]?.value || "LOW",
      });

      setMobileTicketOpen(false);
      setMobileStep(1);
      toast.success("Destek talebiniz başarıyla gönderildi.");
    } catch (error) {
      console.error("Destek talebi gönderilemedi:", error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!ticketId) {
      toast.error("Silinecek talep ID bilgisi bulunamadı.");
      return;
    }

    try {
      await api.deleteTicket(ticketId);
      await loadTicketsFromApi();

      toast.success("Destek talebi başarıyla silindi.");
    } catch (error) {
      console.error("Destek talebi silinemedi:", error);
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <AdminLayout>
      <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
        <section className="rounded-[22px] border border-[#E4E4E7] bg-white p-[18px]">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-extrabold tracking-[0.08em] text-[#EA580C]">
                DESTEK MERKEZİ
              </p>
              <h1 className="mt-1 text-[22px] font-extrabold leading-7 text-[#18181B]">
                Size yardımcı olalım
              </h1>
              <p className="mt-1.5 text-[13px] leading-[19px] text-[#71717A]">
                Teknik sorunlarınızı, ödeme sorularınızı veya geliştirme
                taleplerinizi ekibimize iletin.
              </p>
            </div>

            <div className="flex min-h-[30px] shrink-0 items-center gap-1.5 rounded-full bg-green-100 px-2.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
              <span className="text-[10px] font-bold text-green-700">
                Sistem aktif
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={openMobileTicket}
            className="mt-[18px] flex min-h-12 w-full items-center justify-center rounded-[15px] bg-[#EA580C] px-4 text-[15px] font-bold text-white active:opacity-70"
          >
            + Yeni Destek Talebi
          </button>
        </section>

        <div className="mt-6 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[21px] font-extrabold leading-7 text-[#18181B]">
              Son Talepler
            </h2>
            <p className="mt-0.5 text-[12px] leading-[17px] text-[#71717A]">
              Gönderdiğiniz destek taleplerini buradan takip edin.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Destek taleplerini yenile"
              disabled={ticketsLoading}
              onClick={loadTicketsFromApi}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E4E7] bg-white text-[#71717A] active:bg-[#F4F4F5] disabled:opacity-40"
            >
              <RefreshCw
                className={`h-4 w-4 ${ticketsLoading ? "animate-spin" : ""}`}
              />
            </button>

            <div className="flex h-8 min-w-8 items-center justify-center rounded-full bg-[#FFF7ED] px-2 text-xs font-extrabold text-[#EA580C]">
              {ticketsLoading ? "…" : stats.total}
            </div>
          </div>
        </div>

        {stats.critical > 0 ? (
          <div className="mt-3 flex min-h-10 items-center gap-2 rounded-xl bg-[#FEF2F2] px-3">
            <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#DC2626]" />
            <p className="text-xs font-semibold text-[#991B1B]">
              {stats.critical} kritik öncelikli talebiniz bulunuyor.
            </p>
          </div>
        ) : null}

        <div className="mt-3">
          {ticketsLoading && recentTickets.length === 0 ? (
            <div className="rounded-[20px] border border-[#E4E4E7] bg-white p-5">
              <div className="h-4 w-2/3 animate-pulse rounded-full bg-[#E4E4E7]" />
              <div className="mt-3 h-3 w-1/2 animate-pulse rounded-full bg-[#F4F4F5]" />
              <div className="mt-5 h-16 animate-pulse rounded-[14px] bg-[#F4F4F5]" />
            </div>
          ) : recentTickets.length ? (
            <div className="space-y-3">
              {recentTickets.map((ticket) => {
                const priorityLabel =
                  ticket.priorityLabel || getPriorityLabel(ticket.priority);
                const isCritical = ticket.priority === "CRITICAL";
                const isHigh = ticket.priority === "HIGH";

                return (
                  <article
                    key={ticket.id || ticket.localId}
                    className="rounded-[18px] border border-[#E4E4E7] bg-white p-[15px]"
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-[15px] font-extrabold leading-5 text-[#18181B]">
                          {ticket.subject}
                        </h3>
                        <p className="mt-0.5 text-[11px] text-[#A1A1AA]">
                          {formatDate(ticket.createdAt)}
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className="rounded-full bg-[#F4F4F5] px-2.5 py-1.5 text-[9px] font-bold text-[#52525B]">
                          {ticket.status}
                        </span>

                        {ticket.id ? (
                          <button
                            type="button"
                            aria-label={`${ticket.subject} talebini sil`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Bu destek talebini silmek istiyor musunuz?"
                                )
                              ) {
                                handleDeleteTicket(ticket.id);
                              }
                            }}
                            className="flex h-11 w-11 items-center justify-center rounded-full text-[#DC2626] active:bg-[#FEF2F2]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <span className="rounded-[9px] bg-[#F4F4F5] px-2 py-1.5 text-[10px] font-bold text-[#52525B]">
                        {ticket.issueTypeLabel || ticket.issueType || "Destek"}
                      </span>

                      <span
                        className={`rounded-[9px] px-2 py-1.5 text-[10px] font-bold ${
                          isCritical
                            ? "bg-[#FEF2F2] text-[#DC2626]"
                            : isHigh
                            ? "bg-[#FFF7ED] text-[#EA580C]"
                            : "bg-[#F4F4F5] text-[#52525B]"
                        }`}
                      >
                        {priorityLabel}
                      </span>
                    </div>

                    <p className="mt-2.5 line-clamp-3 text-[12px] leading-[18px] text-[#52525B]">
                      {ticket.message}
                    </p>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[230px] flex-col items-center justify-center rounded-[20px] border border-[#E4E4E7] bg-white px-7 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF7ED] text-xl font-extrabold text-[#EA580C]">
                ?
              </div>
              <h3 className="mt-3.5 text-[17px] font-extrabold text-[#18181B]">
                Henüz destek talebiniz yok
              </h3>
              <p className="mt-1.5 text-[13px] leading-[19px] text-[#71717A]">
                Bir sorun yaşadığınızda yeni destek talebi oluşturabilirsiniz.
              </p>
              <button
                type="button"
                onClick={openMobileTicket}
                className="mt-4 min-h-11 rounded-[13px] bg-[#EA580C] px-4 text-[13px] font-bold text-white"
              >
                Yeni Talep Oluştur
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={mobileTicketOpen}
        onOpenChange={(open) => {
          if (!open) closeMobileTicket();
        }}
      >
        <DialogContent className="bottom-0 left-0 right-0 top-auto max-h-[84dvh] w-full max-w-none translate-x-0 translate-y-0 overflow-hidden rounded-b-none rounded-t-[24px] border-x-0 border-b-0 p-0 md:hidden">
          <form
            onSubmit={handleSubmit}
            className="flex max-h-[84dvh] min-h-0 flex-col"
          >
            <DialogHeader className="shrink-0 border-b border-[#E4E4E7] px-5 pb-4 pt-5 text-left">
              <p className="text-[10px] font-extrabold tracking-[0.07em] text-[#EA580C]">
                DESTEK
              </p>
              <DialogTitle className="text-[23px] font-extrabold leading-7 text-[#18181B]">
                Yeni Destek Talebi
              </DialogTitle>
              <p className="max-w-[330px] text-[13px] leading-[19px] text-[#71717A]">
                {mobileStep === 1
                  ? "Talebinizin konusunu belirleyin."
                  : "Sorunu mümkün olduğunca açık şekilde anlatın."}
              </p>

              <div className="pt-2">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-[#18181B]">
                    {mobileStep === 1 ? "Talep Bilgileri" : "Açıklama"}
                  </span>
                  <span className="text-[11px] font-bold text-[#A1A1AA]">
                    {mobileStep} / 2
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <span className="h-1 rounded-full bg-[#EA580C]" />
                  <span
                    className={`h-1 rounded-full ${
                      mobileStep === 2 ? "bg-[#EA580C]" : "bg-[#E4E4E7]"
                    }`}
                  />
                </div>
              </div>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
              {mobileStep === 1 ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="mobile-support-subject"
                      className="block text-[13px] font-bold text-[#3F3F46]"
                    >
                      Talep başlığı
                    </label>
                    <input
                      id="mobile-support-subject"
                      value={formData.subject}
                      onChange={(event) =>
                        updateForm("subject", event.target.value)
                      }
                      placeholder="Örn: Menü görselleri mobilde bozuluyor"
                      autoComplete="off"
                      className="h-[52px] w-full rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-base text-[#18181B] outline-none focus:border-[#EA580C]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="mobile-support-type"
                      className="block text-[13px] font-bold text-[#3F3F46]"
                    >
                      Talep türü
                    </label>
                    <select
                      id="mobile-support-type"
                      value={formData.issueType}
                      onChange={(event) =>
                        updateForm("issueType", event.target.value)
                      }
                      disabled={constantsLoading}
                      className="h-[52px] w-full rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-base text-[#18181B] outline-none focus:border-[#EA580C] disabled:opacity-50"
                    >
                      <option value="">
                        {constantsLoading
                          ? "Yükleniyor..."
                          : "Bir kategori seçin"}
                      </option>
                      {(issueTypes.length ? issueTypes : ISSUE_TYPES).map(
                        (item) => (
                          <option key={item.value} value={item.value}>
                            {item.label || item.name || item.value}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="mobile-support-priority"
                      className="block text-[13px] font-bold text-[#3F3F46]"
                    >
                      Öncelik
                    </label>
                    <select
                      id="mobile-support-priority"
                      value={formData.priority}
                      onChange={(event) =>
                        updateForm("priority", event.target.value)
                      }
                      disabled={constantsLoading}
                      className="h-[52px] w-full rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-base text-[#18181B] outline-none focus:border-[#EA580C] disabled:opacity-50"
                    >
                      {(priorities.length ? priorities : PRIORITIES).map(
                        (item) => (
                          <option key={item.value} value={item.value}>
                            {item.label || item.name || item.value}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div className="rounded-[15px] bg-[#FFF7ED] p-3.5">
                    <p className="text-xs font-bold text-[#EA580C]">
                      Hesap bilgileriniz otomatik eklenir
                    </p>
                    <p className="mt-1 text-[11px] leading-4 text-[#9A3412]">
                      İletişim ve işletme bilgileriniz destek talebine sistem
                      tarafından ilişkilendirilir.
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    <span className="rounded-full bg-[#F4F4F5] px-2.5 py-1.5 text-[10px] font-bold text-[#52525B]">
                      {selectedIssueTypeLabel}
                    </span>
                    <span className="rounded-full bg-[#FFF7ED] px-2.5 py-1.5 text-[10px] font-bold text-[#EA580C]">
                      {selectedPriorityLabel}
                    </span>
                  </div>

                  <label
                    htmlFor="mobile-support-message"
                    className="block text-[13px] font-bold text-[#3F3F46]"
                  >
                    Talep açıklaması
                  </label>
                  <textarea
                    id="mobile-support-message"
                    autoFocus
                    rows={7}
                    value={formData.message}
                    onChange={(event) =>
                      updateForm("message", event.target.value)
                    }
                    placeholder="Sorunun ne zaman başladığını, hangi sayfada olduğunu ve beklenen davranışı açıklayın."
                    className="mt-1.5 min-h-[180px] w-full resize-none rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 py-3 text-base leading-6 text-[#18181B] outline-none focus:border-[#EA580C]"
                  />

                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={`text-[11px] font-semibold ${
                        formData.message.trim().length >= 20
                          ? "text-green-600"
                          : "text-[#A1A1AA]"
                      }`}
                    >
                      En az 20 karakter
                    </span>
                    <span className="text-[11px] tabular-nums text-[#A1A1AA]">
                      {formData.message.trim().length}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-[#E4E4E7] bg-white px-5 py-3">
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  if (mobileStep === 1) {
                    closeMobileTicket();
                    return;
                  }
                  setMobileStep(1);
                }}
                className="h-[52px] rounded-[15px]"
              >
                {mobileStep === 1 ? "Vazgeç" : "Geri"}
              </Button>

              {mobileStep === 1 ? (
                <Button
                  type="button"
                  disabled={submitting}
                  onClick={handleMobileNext}
                  className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
                >
                  Devam
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={
                    submitting || formData.message.trim().length < 20
                  }
                  className="h-[52px] rounded-[15px] bg-[#EA580C] hover:bg-[#EA580C]"
                >
                  {submitting ? "Gönderiliyor..." : "Talebi Gönder"}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <div className="hidden md:block">
      <div className="w-full max-w-6xl mx-auto space-y-6 md:space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1
              className="text-2xl md:text-3xl font-bold tracking-tight"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Destek Talebi
            </h1>
            <p className="text-sm md:text-base text-zinc-600 mt-2 max-w-2xl">
              Teknik sorunları, geliştirme isteklerini ve operasyonel ihtiyaçları
              tek bir form üzerinden bize iletin.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
            <Sparkles className="h-4 w-4" />
            Destek sistemi aktif
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-3 text-zinc-500">
              <MessageSquare className="h-5 w-5" />
              Son Talepler
            </div>
            <div className="mt-3 text-3xl font-semibold text-zinc-950">
              {ticketsLoading ? "..." : stats.total}
            </div>
            <p className="mt-2 text-sm text-zinc-600">
              Backend üzerinden listelenen destek talepleriniz burada görünür.
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-3 text-zinc-500">
              <AlertCircle className="h-5 w-5" />
              Kritik İşaretli
            </div>
            <div className="mt-3 text-3xl font-semibold text-zinc-950">
              {ticketsLoading ? "..." : stats.critical}
            </div>
            <p className="mt-2 text-sm text-zinc-600">
              Hızlı aksiyon gerektiren talepleri ayrı önceliklendirebilirsiniz.
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-3 text-zinc-500">
              <Clock3 className="h-5 w-5" />
              Hedef Yanıt
            </div>
            <div className="mt-3 text-3xl font-semibold text-zinc-950">24s</div>
            <p className="mt-2 text-sm text-zinc-600">
              Talepleriniz destek ekibine iletilir ve önceliğine göre
              değerlendirilir.
            </p>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-zinc-200 bg-white p-5 md:p-7"
          >
            <div className="flex items-start gap-4 border-b border-zinc-100 pb-5">
              <div className="rounded-2xl bg-zinc-950 p-3 text-white">
                <LifeBuoy className="h-5 w-5" />
              </div>

              <div>
                <h2
                  className="text-xl font-semibold text-zinc-950"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Yeni Talep Oluştur
                </h2>
                <p className="mt-1 text-sm text-zinc-600">
                  Hesap ve işletme bilgileriniz sistemden otomatik alınır.
                  Sadece talebinizi açıklamanız yeterlidir.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-5 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="subject">Talep Başlığı</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(event) =>
                    updateForm("subject", event.target.value)
                  }
                  placeholder="Örn. Menü görselleri mobilde bozuluyor"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="issueType">Talep Türü</Label>
                <select
                  id="issueType"
                  value={formData.issueType}
                  onChange={(event) =>
                    updateForm("issueType", event.target.value)
                  }
                  disabled={constantsLoading}
                  className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition focus:border-zinc-400 focus:ring-2 focus:ring-zinc-950/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">
                    {constantsLoading ? "Yükleniyor..." : "Bir kategori seçin"}
                  </option>

                  {(issueTypes.length ? issueTypes : ISSUE_TYPES).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label || item.name || item.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Öncelik</Label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(event) =>
                    updateForm("priority", event.target.value)
                  }
                  disabled={constantsLoading}
                  className="flex h-10 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition focus:border-zinc-400 focus:ring-2 focus:ring-zinc-950/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {(priorities.length ? priorities : PRIORITIES).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label || item.name || item.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="message">Talep Açıklaması</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(event) =>
                    updateForm("message", event.target.value)
                  }
                  placeholder="Sorunun ne zaman başladığını, hangi sayfada olduğunu ve mümkünse beklenen davranışı yazın."
                  className="min-h-[180px] resize-none"
                />
                <p className="text-xs text-zinc-500">
                  En az 20 karakter yazmalısınız. Sorunu net anlatmanız çözüm
                  sürecini hızlandırır.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-zinc-100 pt-5 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-zinc-500">
                Talebiniz destek sistemine gönderilir ve liste backend üzerinden
                güncellenir.
              </p>

              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-zinc-950 px-6 hover:bg-zinc-800"
              >
                <Send className="mr-2 h-4 w-4" />
                {submitting ? "Gönderiliyor..." : "Talebi Gönder"}
              </Button>
            </div>
          </form>

          <div className="space-y-6">
            <div className="rounded-3xl border border-zinc-200 bg-white p-5 md:p-6">
              <h2
                className="text-xl font-semibold text-zinc-950"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Hesap Bilgileri
              </h2>

              <div className="mt-4 space-y-3 text-sm">
                <div className="rounded-2xl bg-zinc-50 p-4">
                  <div className="text-xs text-zinc-500">İşletme</div>
                  <div className="mt-1 font-medium text-zinc-900">
                    {userInfo.restaurantName || "İşletme bilgisi bulunamadı"}
                  </div>
                </div>

                <div className="rounded-2xl bg-zinc-50 p-4">
                  <div className="text-xs text-zinc-500">İletişim</div>
                  <div className="mt-1 font-medium text-zinc-900">
                    {userInfo.contactName || "Kullanıcı bilgisi bulunamadı"}
                  </div>

                  {userInfo.contactEmail && (
                    <div className="mt-1 text-zinc-600">
                      {userInfo.contactEmail}
                    </div>
                  )}

                  {userInfo.contactPhone && (
                    <div className="mt-1 text-zinc-600">
                      {userInfo.contactPhone}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-5 md:p-6">
              <h2
                className="text-xl font-semibold text-zinc-950"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Hızlı Notlar
              </h2>

              <div className="mt-4 space-y-4 text-sm text-zinc-600">
                <div className="rounded-2xl bg-zinc-50 p-4">
                  Başlıkta sorunu kısa ve net yazın. Açıklama alanında ise adım
                  adım bağlam verin.
                </div>

                <div className="rounded-2xl bg-zinc-50 p-4">
                  Kritik talepleri sadece ödeme, erişim veya işletme kullanımını
                  tamamen engelleyen durumlarda seçmeniz öncelik yönetimini
                  kolaylaştırır.
                </div>

                <div className="rounded-2xl bg-zinc-50 p-4">
                  Talebiniz gönderildiğinde hesap bilgileriniz backend tarafında
                  token üzerinden eşleştirilir.
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-5 md:p-6">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-zinc-500" />
                <h2 className="text-lg font-semibold text-zinc-950">
                  Son Gönderilen Talepler
                </h2>
              </div>

              <div className="mt-4 space-y-3">
                {ticketsLoading ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
                    Destek talepleri yükleniyor...
                  </div>
                ) : recentTickets.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
                    Henüz gönderilmiş bir destek talebi yok.
                  </div>
                ) : (
                  recentTickets.map((ticket) => (
                    <div
                      key={ticket.id || ticket.localId}
                      className="rounded-2xl border border-zinc-100 bg-zinc-50 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-zinc-900">
                            {ticket.subject}
                          </div>

                          <div className="mt-1 text-xs text-zinc-500">
                            {formatDate(ticket.createdAt)}
                          </div>

                          {ticket.issueTypeLabel && (
                            <div className="mt-2 inline-flex rounded-full bg-white px-3 py-1 text-xs font-medium text-zinc-600">
                              {ticket.issueTypeLabel}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-zinc-700">
                            {ticket.priorityLabel ||
                              getPriorityLabel(ticket.priority)}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleDeleteTicket(ticket.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-zinc-400 transition hover:bg-red-50 hover:text-red-600"
                            title="Talebi sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <p className="mt-3 line-clamp-3 text-sm text-zinc-600">
                        {ticket.message}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}
